import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Paper,
  Link as MuiLink,
  useTheme,
} from '@mui/material';
import { DirectionsRun, Favorite, Checkroom } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { BackToTop } from './BackToTop';
import { trackDonation } from '../../configuration/analytics';

const contentUseCase = new ContentUseCase();

export const HomePage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { fundraiserInfo, donationInfo, runningEvents } = contentUseCase.getAllContent(language);

  const handleDonationClick = (cause?: string) => {
    trackDonation(undefined, cause);
  };

  // Derived from the event list so the numbers never go stale
  const countryCount = new Set(
    runningEvents.map((event) => event.location.split(',').pop()?.trim()).filter(Boolean)
  ).size;

  const stats = [
    { value: fundraiserInfo.stats.shirtsValue, label: fundraiserInfo.stats.shirtsLabel },
    { value: `${runningEvents.length}`, label: fundraiserInfo.stats.runsLabel },
    { value: `${countryCount}`, label: fundraiserInfo.stats.countriesLabel },
    { value: fundraiserInfo.stats.priceValue, label: fundraiserInfo.stats.priceLabel },
  ];

  return (
    <Box sx={{ margin: 0, padding: 0 }}>
      <SEO page="home" />
      {/* Hero Section */}
      <Paper
        sx={{
          position: 'relative',
          backgroundColor: 'grey.900',
          color: '#fff',
          mb: theme.spacing(4),
          mt: 0,
          marginTop: 0,
          // Pull up to eliminate the gap between nav bar and hero section
          transform: { xs: 'translateY(-3px)', md: 'translateY(-15px)' },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(https://images.unsplash.com/photo-1552674605-db6ffd4facb5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h1" component="h1" gutterBottom sx={{ mb: theme.spacing(4), fontWeight: 700 }}>
              {fundraiserInfo.hero.title}
            </Typography>
            <Typography variant="h4" paragraph sx={{ mb: theme.spacing(2), maxWidth: { xs: '100%', md: 800 }, mx: 'auto' }}>
              {fundraiserInfo.hero.subtitle}
            </Typography>
            <Typography variant="h6" paragraph sx={{ mb: theme.spacing(4), maxWidth: { xs: '100%', md: 700 }, mx: 'auto', fontWeight: 400, color: 'rgba(255,255,255,0.9)' }}>
              {fundraiserInfo.hero.tagline}
            </Typography>
            <Box sx={{ display: 'flex', gap: theme.spacing(3), justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/mitmachen"
                sx={{ px: theme.spacing(4), py: 1.5, minWidth: 200 }}
              >
                {fundraiserInfo.hero.buttons.joinUs}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/timeline"
                sx={{ px: theme.spacing(4), py: 1.5, minWidth: 200, color: 'white', borderColor: 'white' }}
              >
                {fundraiserInfo.hero.buttons.viewTimeline}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/me-info"
                sx={{ px: theme.spacing(4), py: 1.5, minWidth: 200, color: 'white', borderColor: 'white' }}
              >
                {fundraiserInfo.hero.buttons.learnAboutME}
              </Button>
            </Box>
          </Box>
        </Container>

        {/* Unsplash Attribution */}
        <Box
          sx={{
            position: 'absolute',
            bottom: theme.spacing(0.5),
            right: theme.spacing(1),
            zIndex: 10,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.65rem',
              fontWeight: 400,
            }}
          >
            {fundraiserInfo.hero.attribution.photoBy}{' '}
            <a
              href="https://unsplash.com/@unsplash"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'underline',
                fontWeight: 400,
              }}
            >
              Unsplash
            </a>
          </Typography>
        </Box>
      </Paper>

      {/* Social proof band */}
      <Container maxWidth="md" sx={{ mb: theme.spacing(7), mt: theme.spacing(-1) }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${stats.length}, 1fr)` },
            gap: theme.spacing(2),
            textAlign: 'center',
          }}
        >
          {stats.map(({ value, label }, index) => (
            <Box
              key={label}
              sx={{
                py: theme.spacing(1),
                // odd count: let the last one centre itself across the two mobile columns
                gridColumn: {
                  xs: index === stats.length - 1 && stats.length % 2 === 1 ? 'span 2' : 'auto',
                  md: 'auto',
                },
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontSize: { xs: '1.35rem', md: '1.6rem' },
                  fontWeight: 700,
                  color: 'primary.main',
                  lineHeight: 1.1,
                }}
              >
                {value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: theme.spacing(0.5), fontSize: '0.8rem' }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mb: theme.spacing(8) }}>
        {/* Fundraiser Overview */}
        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {fundraiserInfo.title}{' '}
            <Box
              component="img"
              src="/Light_blue_ribbon.svg.png"
              alt="Light Blue Awareness Ribbon"
              sx={{
                height: '50px',
                width: 'auto',
                marginLeft: theme.spacing(2),
                verticalAlign: 'middle',
              }}
            />
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph align="center" sx={{ mb: theme.spacing(4), maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {fundraiserInfo.description}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph align="center" sx={{ mb: theme.spacing(4), maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {fundraiserInfo.personalStory1}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph align="center" sx={{ mb: theme.spacing(6), maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {fundraiserInfo.personalStory2}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: theme.spacing(4),
            justifyContent: 'center',
          }}>
            <Card
              sx={{ flex: 1, textAlign: 'center', maxWidth: { md: 350 }, cursor: 'pointer', '&:hover': { boxShadow: theme.shadows[8] } }}
              component={Link}
              to="/mitmachen"
              style={{ textDecoration: 'none' }}
            >
              <CardContent sx={{ p: theme.spacing(4) }}>
                <Checkroom sx={{ fontSize: theme.spacing(6), color: 'primary.main', mb: theme.spacing(2) }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  {fundraiserInfo.cards.marathon.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {fundraiserInfo.cards.marathon.description}
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{ flex: 1, textAlign: 'center', maxWidth: { md: 350 }, cursor: 'pointer', '&:hover': { boxShadow: theme.shadows[8] } }}
              component={Link}
              to="/timeline"
              style={{ textDecoration: 'none' }}
            >
              <CardContent sx={{ p: theme.spacing(4) }}>
                <DirectionsRun sx={{ fontSize: theme.spacing(6), color: 'primary.main', mb: theme.spacing(2) }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  {fundraiserInfo.cards.research.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {fundraiserInfo.cards.research.description}
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{ flex: 1, textAlign: 'center', maxWidth: { md: 350 }, cursor: 'pointer', '&:hover': { boxShadow: theme.shadows[8] } }}
              component={Link}
              to="/donate"
              style={{ textDecoration: 'none' }}
            >
              <CardContent sx={{ p: theme.spacing(4) }}>
                <Favorite sx={{ fontSize: theme.spacing(6), color: 'secondary.main', mb: theme.spacing(2) }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  {fundraiserInfo.cards.awareness.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {fundraiserInfo.cards.awareness.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* How it works */}
      <Box sx={{ backgroundColor: 'grey.50', py: theme.spacing(8), mb: theme.spacing(8) }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
            {fundraiserInfo.howItWorks.title}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: theme.spacing(4),
            justifyContent: 'center',
            mb: theme.spacing(6),
          }}>
            {fundraiserInfo.howItWorks.steps.map((step) => (
              <Box key={step.number} sx={{ flex: 1, maxWidth: { md: 340 }, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: theme.spacing(2),
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  {step.number}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom>
                  {step.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/mitmachen"
              sx={{ px: theme.spacing(6), py: theme.spacing(2) }}
            >
              {fundraiserInfo.howItWorks.button}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Combined Awareness and Donation Section */}
      <Container maxWidth="xl" sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h2" component="h2" gutterBottom align="center" color="primary">
          {fundraiserInfo.makeDifference.title}
        </Typography>

        {/* Awareness Message */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Box sx={{
            width: '100px',
            height: '2px',
            backgroundColor: 'primary.main',
            mx: 'auto',
            mb: theme.spacing(4),
          }} />
          <Typography variant="h4" gutterBottom align="center">
            {fundraiserInfo.awareness.title}
          </Typography>
          <Typography
            variant="h6"
            paragraph
            sx={{ mb: theme.spacing(4), color: 'text.secondary', textAlign: 'center' }}
          >
            {fundraiserInfo.awareness.subtitle}
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{
              mb: theme.spacing(4),
              color: 'text.secondary',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              textAlign: 'center'
            }}
          >
            {fundraiserInfo.awareness.description}
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(6),
        }} />

        {/* Join Us Section */}
        <Box sx={{ textAlign: 'center', mb: theme.spacing(8) }}>
          <Typography variant="h4" gutterBottom>
            {fundraiserInfo.joinUs.title}
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: theme.spacing(2), color: 'text.secondary' }}>
            {fundraiserInfo.joinUs.subtitle}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary', maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {fundraiserInfo.joinUs.description}
          </Typography>
          <Typography variant="body1" sx={{ mb: theme.spacing(1) }}>
            <MuiLink
              component={Link}
              to="/mitmachen"
              underline="always"
              sx={{ fontWeight: 600 }}
            >
              {fundraiserInfo.joinUs.linkText}
            </MuiLink>
          </Typography>
          <Typography variant="body1" color="primary" sx={{ fontWeight: 500 }}>
            {fundraiserInfo.joinUs.contactInfo}
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(6),
        }} />

        {/* Donation Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            {fundraiserInfo.donation.title}
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary' }}>
            {fundraiserInfo.donation.subtitle}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: theme.spacing(6), color: 'text.secondary', maxWidth: { xs: '100%', md: 1200 }, mx: 'auto' }}>
            {fundraiserInfo.donation.impact}
          </Typography>

          {/* Causes Overview */}
          <Box sx={{ mb: theme.spacing(6) }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: theme.spacing(3),
              justifyContent: 'center',
              mb: theme.spacing(4),
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
            }}>
              {donationInfo.causes.map((cause) => (
                <Card key={`homepage-cause-${cause.title.toLowerCase().replace(/\s+/g, '-')}`} sx={{ flex: 1, maxWidth: { md: 400 } }}>
                  <CardContent sx={{ p: theme.spacing(3) }}>
                    <Typography variant="h6" component="h3" gutterBottom color="primary">
                      {cause.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(2) }}>
                      {cause.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: theme.spacing(3), justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/donate"
              onClick={() => handleDonationClick('homepage_final_cta')}
              sx={{ px: theme.spacing(6), py: theme.spacing(2) }}
            >
              {fundraiserInfo.donation.supportButton}
            </Button>
          </Box>
        </Box>
      </Container>
      <BackToTop />
    </Box>
  );
};
